import StatusBadge from './tracking/StatusBadge.jsx'
import TrackingStepper from './tracking/TrackingStepper.jsx'
import DeliveryEta from './tracking/DeliveryEta.jsx'
import CourierDetails from './tracking/CourierDetails.jsx'
import TrackingTimeline from './tracking/TrackingTimeline.jsx'
import NdrResponseCard from './tracking/NdrResponseCard.jsx'
import { stageOf } from './tracking/trackingUtils'

const PACKING_STATUSES = new Set(['pending', 'confirmed', 'processing'])

/**
 * Courier side of an order (GET /orders/track or public tracking →
 * `shipment`), live from Shiprocket: the API re-checks the courier whenever
 * its copy is a few minutes old. Before the order has an AWB this shows the
 * stepper and a "being packed" note. Sections live in ./tracking/.
 *
 * `onNdrRespond(body)` answers a failed delivery (omit it to hide the button);
 * `onRefresh` reloads the data after an answer. `withStepper` adds the
 * delivery stepper - off on the order page, which has its own progress bar.
 */
export default function ShipmentTracking({ shipment, orderStatus, onNdrRespond, onRefresh, withStepper = false }) {
  if (!shipment) {
    if (!PACKING_STATUSES.has(String(orderStatus ?? '').toLowerCase())) return null
    return (
      <Card>
        {withStepper && <TrackingStepper stage={null} orderStatus={orderStatus} />}
        <p className={`${withStepper ? 'mt-6 ' : ''}text-sm text-primary/50 tracking-wider normal-case`}>
          Your order is being prepared. Courier and tracking details will appear here as soon as it's handed to the courier.
        </p>
      </Card>
    )
  }

  const stage = stageOf(shipment)
  const returnShipment = shipment.return_shipment

  return (
    <Card badge={<StatusBadge stage={stage} />}>
      {/* Announced to screen readers when the status changes on a refresh. */}
      <div aria-live="polite" className="space-y-6">
        {withStepper && !['cancelled', 'lost'].includes(stage) && <TrackingStepper stage={stage} orderStatus={orderStatus} />}
        <DeliveryEta shipment={shipment} stage={stage} />
      </div>

      {stage === 'delivery_failed' && shipment.ndr && (
        <div className="mt-6">
          <NdrResponseCard ndr={shipment.ndr} onRespond={onNdrRespond} onResponded={onRefresh} />
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-dashed border-border/60">
        <CourierDetails shipment={shipment} />
      </div>

      <div className="mt-7">
        <TrackingTimeline shipment={shipment} />
      </div>

      {returnShipment && (
        <div className="mt-8 pt-6 border-t border-dashed border-border/60">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h3 className="text-sm font-heading text-primary tracking-widest">Return Pickup</h3>
            <StatusBadge stage={stageOf(returnShipment)} />
          </div>
          <CourierDetails shipment={returnShipment} />
          <div className="mt-6">
            <TrackingTimeline shipment={returnShipment} />
          </div>
        </div>
      )}
    </Card>
  )
}

function Card({ badge, children }) {
  return (
    <section className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8" aria-labelledby="shipment-tracking-title">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary" aria-hidden="true">
            <i className="fa-solid fa-truck-fast"></i>
          </div>
          <h2 id="shipment-tracking-title" className="text-base font-heading text-primary tracking-widest">
            Shipment Tracking
          </h2>
        </div>
        {badge}
      </div>
      {children}
    </section>
  )
}
